import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  useTheme,
  InputAdornment,
  TextField,
  Button,
  Paper,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Search as SearchIcon, Add as AddIcon, Remove as RemoveIcon, AddShoppingCart as AddShoppingCartIcon } from "@mui/icons-material";
import LoadingComponent from "../components/LoadingComponent";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getProduct } from "../redux/slices/product/thunk";
import { addToCart, getCartCounter } from "../redux/slices/cart/thunk";
import { getOrdersCounter } from "../redux/slices/order/thunk";

const ServiceCard = React.memo(({ service, t, theme, navigate, user, addToCartLoading, setAddToCartLoading, dispatch }) => {
  const [selectedQty, setSelectedQty] = useState(1);
  const outOfStock = service.stock_limit === true && service.count_in_stock === 0;

  useEffect(() => {
    setSelectedQty(1);
  }, [service._id]);

  const handleIncreaseQty = () => {
    if (outOfStock) return;
    if (!service.stock_limit || selectedQty < service.count_in_stock) {
      setSelectedQty(prev => prev + 1);
    }
  };

  const handleDecreaseQty = () => {
    if (outOfStock) return;
    if (selectedQty > 1) {
      setSelectedQty(prev => prev - 1);
    }
  };

  const handleAddToCart = async () => {
    if (outOfStock) return;
    setAddToCartLoading(prev => ({ ...prev, [service._id]: true }));
    
    try {
      await dispatch(addToCart({
        data: {
          familyId: service?.familyId?._id,
          productId: service._id,
          userId: user?._id,
          quantity: selectedQty
        }
      }));
       await dispatch(getCartCounter({ id: user?._id }));
    } catch (error) {
      console.error("Add to cart error:", error);
    } finally {
      setAddToCartLoading(prev => ({ ...prev, [service._id]: false }));
      setSelectedQty(1);
    }
  };

  const isAdding = !!addToCartLoading[service._id];
  const discountedPrice = service.discount > 0 
    ? (service.price - (service.price * service.discount) / 100).toFixed(2)
    : service.price.toFixed(2);

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.3s, box-shadow 0.3s",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: theme.shadows[6],
        },
      }}
    >
      {/* Image */}
      <Box sx={{ position: "relative", width: "100%", pt: "56.25%" }}>
        <Box
          component="img"
          src={service.image}
          alt={service.name}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.5s ease",
            "&:hover": { transform: "scale(1.05)" },
          }}
        />
        {service.discount > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "error.main",
              color: "white",
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontWeight: "bold",
              fontSize: "0.8rem",
            }}
          >
            {`${service.discount}% OFF`}
          </Box>
        )}
      </Box>
      
      {/* Details */}
      <Box sx={{ p: 2, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {service.name}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            flexGrow: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {service.description}
        </Typography>
        
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="h6"
              sx={{
                color: service.discount > 0 ? "error.main" : "text.primary",
                fontWeight: 700,
              }}
            >
              {discountedPrice} <span>{t("rs")}</span>
            </Typography>
            {service.discount > 0 && (
              <Typography
                variant="body2"
                sx={{
                  textDecoration: "line-through",
                  color: "text.secondary",
                }}
              >
                {service.price.toFixed(2)} <span>{t("rs")}</span>
              </Typography>
            )}
          </Box>
          
          {service.stock_limit ? (
            <Typography variant="caption" color="text.secondary">
              {t("common.availableQty")}: {service.count_in_stock}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {t("common.unlimitedNote")}
            </Typography>
          )}
        </Box>

        {/* Out of stock message */}
        {outOfStock && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="error" sx={{ fontWeight: 600 }}>
              {t("common.outOfStock")}
            </Typography>
          </Box>
        )}
        
        {/* Quantity selector */}
        {!outOfStock && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
            <IconButton
              onClick={handleDecreaseQty}
              disabled={selectedQty <= 1}
              size="small"
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Box sx={{ minWidth: 32, textAlign: "center" }}>
              <Typography variant="body1">{selectedQty}</Typography>
            </Box>
            <IconButton
              onClick={handleIncreaseQty}
              disabled={service.stock_limit && selectedQty >= service.count_in_stock}
              size="small"
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        
        {/* Actions */}
        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => navigate(`/product/${service._id}`)}
          >
            {t("common.viewDetails")}
          </Button>
          
          {outOfStock ? (
            <Button variant="contained" color="secondary" fullWidth disabled>
              {t("common.outOfStock2")}
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleAddToCart}
              startIcon={
                isAdding ? <CircularProgress size={20} color="inherit" /> : <AddShoppingCartIcon />
              }
              disabled={isAdding}
              sx={{
                "& .MuiButton-startIcon": { marginInlineEnd: theme.spacing(1) },
              }}
            >
              {t("common.addToCart")}
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
});

const Home = () => {
   
  const [addToCartLoading, setAddToCartLoading] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isRtl = i18n.language === "ar";
  const dispatch = useDispatch();
  
  const user = JSON.parse(localStorage.getItem("user"));
  const { error, loading, products } = useSelector(
    (state) => state.productSlice
  );

  console.log("products",products);

  // Load products on mount
  useEffect(() => {
    dispatch(getProduct({ query: null }));
    dispatch(getCartCounter({ id: user?._id }));
    dispatch(getOrdersCounter({ id: user?._id,type:user?.type,status:'pending' }));
    
  }, [dispatch]);

  // State for filtered services
  const [filteredServices, setFilteredServices] = useState([]);

  console.log("filteredServices",filteredServices);
  // Optimized search handler
  const filterServices = useCallback(() => {
    setIsSearching(true);
    const term = searchTerm.trim().toLowerCase();
    
    if (!term) {
      setFilteredServices(Array.isArray(products) ? products : []);
      setIsSearching(false);
      return;
    }

    const source = Array.isArray(products) ? products : [];
    const filtered = source.filter((s) => {
      const name = s.name?.toLowerCase() || "";
      const desc = s.description?.toLowerCase() || "";
      return name.includes(term) || desc.includes(term);
    });
    
    setFilteredServices(filtered);
    setIsSearching(false);
  }, [searchTerm, products]);

  // Debounced search effect
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    if (searchTerm || products.length > 0) {
      setIsSearching(true);
      setSearchTimeout(
        setTimeout(() => {
          filterServices();
        }, 300) // 300ms debounce
      );
    }
    
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTerm, products, filterServices]);

  // Initialize filtered services when products load
  useEffect(() => {
    if (!searchTerm) {
      setFilteredServices(products);
    }
  }, [products, searchTerm]);

  // Memoized service cards
  const serviceCards = useMemo(() => {
    return filteredServices?.map((service) => (
      <Grid item xs={12} sm={6} md={4} lg={4} key={service._id}>
        <ServiceCard 
          service={service}
          t={t}
          theme={theme}
          navigate={navigate}
          user={user}
          addToCartLoading={addToCartLoading}
          setAddToCartLoading={setAddToCartLoading}
          dispatch={dispatch}
        />
      </Grid>
    ));
  }, [filteredServices, t, theme, navigate, user, addToCartLoading, dispatch]);


     if (user?.type !== "client") {
    return <Navigate to="/" replace />;
  }
  
  if (loading && !products.length ) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <LoadingComponent />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box component="main">
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        {/* Title & Subtitle */}
        <Box
          sx={{
            textAlign: "center",
            mb: { xs: 4, md: 6 },
            direction: isRtl ? "rtl" : "ltr",
          }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
              fontWeight: 600,
              mb: 1.5,
              color: theme.palette.mode === "light" ? "#333" : "#fff",
              fontFamily: "Cairo",
            }}
          >
            {t("services.title")}
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: 700,
              mx: "auto",
              fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" },
              fontWeight: 400,
              px: { xs: 2, md: 0 },
              fontFamily: "Cairo",
              opacity: 0.8,
            }}
          >
            {t("services.subtitle")}
          </Typography>
        </Box>

        {/* Search Field */}
        <Box sx={{ mb: 4, maxWidth: 800, mx: "auto" }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t("services.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {isSearching ? <CircularProgress size={24} /> : <SearchIcon />}
                </InputAdornment>
              ),
              sx: { borderRadius: 1, height: 56 },
            }}
          />
        </Box>

        {/* Grid of ServiceCards */}
        <Grid
          container
          spacing={{ xs: 2, sm: 3, md: 4 }}
          sx={{ direction: isRtl ? "rtl" : "ltr" }}
        >
          {filteredServices.length === 0 ? (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  py: 4,
                  textAlign: "center",
                }}
              >
                <Typography variant="h6" sx={{ color: "text.secondary" }}>
                  {searchTerm ? t("services.noResults") : t("services.noServices")}
                </Typography>
              </Box>
            </Grid>
          ) : (
            serviceCards
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default Home;